import { PartialNullable } from "../Extensions";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { PropertyChange } from "../PropertyChange";
import { Property, PropertyBoolean, PropertyString, RadObject } from "../RadObject";
import { AjaxRequest } from "./AjaxRequest";
import { AuthenticationData } from "./AuthenticationData";
import { IAjaxRequest } from "./IAjaxRequest";
import { IWebClient } from "./IWebClient";
import { WebClientPropertyValues } from "./WebClientPropertyValues";
import { NAMESPACE } from "./_Namespace";

export class WebClient extends RadObject implements IWebClient
{
    // #region Static

    public static override classNamespace = NAMESPACE + "WebClient";
    public static override className = "WebClient";

    // #region Global Variables

    public static staticHeaders: { [name: string]: any } = {};

    // #endregion

    // #region Overrides

    public static addStaticHeader(name: string, value: any)
    {
        this.staticHeaders[name] = value;
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public baseUrl!: PropertyString;
    public inhibitNetworkErrors!: PropertyBoolean;
    public onConnected!: Property<() => void>;
    public onDisconnected!: Property<() => void>;
    public onRequestAuthenticationData!: Property<() => AuthenticationData>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<WebClientPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public createHtmlGetRequest(relativeUrl: string, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return new AjaxRequest({
            settings: {
                type: "GET",
                headers: ObjectHelper.extend(true, {}, this._createDefaultHeaders(), additionalHeaders),
                url: this.baseUrl.get() + relativeUrl,
                showLoading: showLoading === true || showLoading === false ? showLoading : true,
                displayExceptions: displayExceptions === true || displayExceptions === false ? displayExceptions : true,
            },
            type: "HTML",
            webClient: this
        });
    }

    // #endregion

    // #region Protected Methods

    protected _baseUrlChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _createAuthenticationHeaders()
    {
        const
            headers: any = {},
            onRequestAuthenticationData = this.onRequestAuthenticationData.get(),
            authenticationData = onRequestAuthenticationData ? onRequestAuthenticationData() : null;

        if (authenticationData)
        {
            switch (authenticationData.scheme)
            {
                case "Bearer":
                    if (authenticationData.accessToken)
                        headers["Authorization"] = `${authenticationData.scheme} ${authenticationData.accessToken}`;
                    break;
                case "Cookies":
                    if (authenticationData.antiForgeryToken)
                        headers["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                    break;
            }
        }

        return headers;
    }
    protected _createDefaultHeaders()
    {
        return ObjectHelper.extend(true, {}, ObjectHelper.cloneObject(WebClient.staticHeaders), this._createAuthenticationHeaders());
    }
    protected _inhibitNetworkErrorsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _onConnectedChanged(change: PropertyChange<() => void>)
    {
        /* Intentionally empty */
    }
    protected _onDisconnectedChanged(change: PropertyChange<() => void>)
    {
        /* Intentionally empty */
    }
    protected _onRequestAuthenticationDataChanged(change: PropertyChange<() => AuthenticationData>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: WebClientPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.baseUrl = new PropertyString(this, propertyValues.baseUrl, this._baseUrlChanged);
        this.inhibitNetworkErrors = new PropertyBoolean(this, propertyValues.inhibitNetworkErrors, this._inhibitNetworkErrorsChanged);
        this.onConnected = new Property<() => void>(this, "System.Object, System.Private.CoreLib", propertyValues.onConnected, this._onConnectedChanged);
        this.onDisconnected = new Property<() => void>(this, "System.Object, System.Private.CoreLib", propertyValues.onDisconnected, this._onDisconnectedChanged);
        this.onRequestAuthenticationData = new Property<() => AuthenticationData>(this, "System.Object, System.Private.CoreLib", propertyValues.onRequestAuthenticationData, this._onRequestAuthenticationDataChanged);
    }

    // #endregion

    // #endregion
}